// src/templateManager/schemas/templateSchema.js
import { z } from 'zod';

/**
 * Zod schema for individual context field
 */
const contextFieldSchema = z.object({
  name: z.string().min(1, 'Field name is required'),
  type: z.enum(['text', 'textarea', 'dropdown', 'multiselect']),
  value: z.union([z.string(), z.array(z.string())]).optional(),
  description: z.string().optional(),
  options: z.array(z.string()).optional(),
  required: z.boolean().optional()
});

/**
 * Zod schema for context fields section
 */
const contextFieldsSchema = z.object({
  useCase: z.array(contextFieldSchema),
  modelContext: z.array(contextFieldSchema),
  businessContext: z.array(contextFieldSchema)
});

/**
 * Zod schema for risk source
 */
const riskSourceSchema = z.object({
  name: z.string().min(1, 'Source name is required'),
  type: z.enum(['text', 'multiselect']),
  value: z.union([z.string(), z.array(z.string())]).optional(),
  options: z.array(z.string()).optional(),
  description: z.string().optional(),
  causes: z.record(z.any()).optional()
});

/**
 * Zod schema for risk area
 */
const riskAreaSchema = z.object({
  label: z.string().min(1, 'Area label is required'),
  description: z.string().optional(),
  riskSources: z.array(riskSourceSchema)
});

/**
 * Zod schema for risk areas (keyed by area name)
 */
const riskAreasSchema = z.record(z.string(), riskAreaSchema);

/**
 * Zod schema for root cause category
 */
const rootCauseCategorySchema = z.object({
  type: z.enum(['multiselect']),
  options: z.array(z.string()),
  description: z.string().optional()
});

/**
 * Zod schema for root causes
 */
const rootCausesSchema = z.object({
  accuracy: rootCauseCategorySchema.optional(),
  fairnessAndBias: rootCauseCategorySchema.optional(),
  performanceAndRobustness: rootCauseCategorySchema.optional(),
  explainabilityAndTransparency: rootCauseCategorySchema.optional()
});

/**
 * Zod schema for cause evaluation criteria
 */
const causeEvaluationCriteriaSchema = z.object({
  entity: z.object({
    type: z.enum(['select']),
    options: z.array(z.string())
  }).optional(),
  intent: z.object({
    type: z.enum(['select']),
    options: z.array(z.string())
  }).optional(),
  timing: z.object({
    type: z.enum(['select']),
    options: z.array(z.string())
  }).optional(),
  other: z.object({
    type: z.enum(['text'])
  }).optional()
});

/**
 * Zod schema for impact category
 */
const impactCategorySchema = z.object({
  id: z.number(),
  name: z.string().min(1, 'Category name is required'),
  description: z.string().optional(),
  order: z.number()
});

/**
 * Zod schema for impact categories array
 */
const impactCategoriesSchema = z.array(impactCategorySchema).optional();

/**
 * Zod schema for risk evaluation criteria (embedded in template)
 * Structure: Array of evaluation attributes with base and deep-dive fields
 */
const riskEvaluationCriteriaSchema = z.array(z.object({
  attribute: z.string(),
  baseEvaluation: z.object({
    name: z.string(),
    value: z.string().optional(),
    fieldType: z.string(),
    options: z.array(z.string())
  }),
  deepDive: z.array(z.object({
    name: z.string(),
    value: z.string().optional(),
    fieldType: z.string(),
    options: z.array(z.string())
  })).optional()
})).optional();

/**
 * Zod schema for control evaluation criteria (embedded in template)
 * Structure: Array of evaluation attributes with properties
 */
const controlEvaluationCriteriaSchema = z.array(z.object({
  attribute: z.string(),
  description: z.string().optional(),
  properties: z.array(z.object({
    name: z.string(),
    fieldType: z.string(),
    options: z.array(z.string()).optional(),
    required: z.boolean().optional(),
    description: z.string().optional(),
    defaultValue: z.string().optional()
  }))
})).optional();

/**
 * Zod schema for complete taxonomy
 */
const taxonomySchema = z.object({
  contextFields: contextFieldsSchema,
  riskAreas: riskAreasSchema,
  rootCauses: rootCausesSchema,
  causeEvaluationCriteria: causeEvaluationCriteriaSchema.optional(),
  impactCategories: impactCategoriesSchema,
  riskEvaluationCriteria: riskEvaluationCriteriaSchema,
  controlEvaluationCriteria: controlEvaluationCriteriaSchema,
  securityTaxonomy: z.any(), // Complex nested structure, validated separately
  securityImpactCategories: z.any().optional(),
  vulnerabilityAssessment: z.any().optional()
});

/**
 * Zod schema for evaluation criteria references
 */
const evaluationCriteriaRefsSchema = z.object({
  riskEvaluationConfigId: z.string().min(1, 'Risk evaluation config is required'),
  controlEvaluationConfigId: z.string().min(1, 'Control evaluation config is required')
});

/**
 * Zod schema for RAG configuration
 */
const ragConfigSchema = z.object({
  useScenarioRag: z.boolean().optional(),
  useControlRag: z.boolean().optional(),
  riskCatalogues: z.array(z.string()).optional(),
  policyTypes: z.array(z.enum(['governance', 'security', 'ai-control'])).optional(),
  policyDocuments: z.array(z.string()).optional()
});

/**
 * Main template schema for form validation
 */
export const templateSchema = z.object({
  name: z.string().min(1, 'Template name is required').max(255, 'Name too long'),
  description: z.string().max(1000, 'Description too long').optional(),
  modelType: z.enum(['generative', 'predictive', 'custom', 'use_case', 'business_case'], {
    errorMap: () => ({ message: 'Invalid model type' })
  }),
  isDefault: z.boolean().optional(),
  taxonomy: taxonomySchema,
  evaluationCriteriaRefs: evaluationCriteriaRefsSchema.optional(),
  ragConfig: ragConfigSchema.optional()
});

/**
 * Schema for template overview (metadata only)
 */
export const templateOverviewSchema = z.object({
  name: z.string().min(1, 'Template name is required').max(255, 'Name too long'),
  description: z.string().max(1000, 'Description too long').optional(),
  modelType: z.enum(['generative', 'predictive', 'custom']),
  isDefault: z.boolean().optional()
});

export default templateSchema;
